<template>
    <Head title="Billing & Subscription" />

    <div class="max-w-6xl mx-auto space-y-6">
      <!-- Header -->
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
          Billing & Subscription
        </h1>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Manage your subscription, payment methods, and billing information
        </p>
      </div>

      <!-- Error Message -->
      <Alert v-if="error" variant="danger">
        {{ error }}
      </Alert>

      <!-- Current Subscription -->
      <Card>
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
            Current Subscription
          </h2>
          <Link v-if="subscription && !subscription.has_scheduled_change && !subscription.is_cancelled" :href="route('settings.subscription.change-plan')">
            <Button variant="secondary" size="sm">
              Change Plan
            </Button>
          </Link>
        </div>

        <div v-if="subscription" class="space-y-4">
          <!-- Plan Details -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Plan</p>
              <p class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white">
                {{ subscription?.plan?.name || 'N/A' }}
              </p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Price</p>
              <p class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white">
                £{{ subscription?.plan?.billing_period === 'annual' ? (subscription?.plan?.price_annual || 0) : (subscription?.plan?.price_monthly || 0) }} / {{ subscription?.plan?.billing_period || 'month' }}
              </p>
            </div>
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Status</p>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="getStatusClass(subscription.status)"
              >
                {{ subscription.status }}
              </span>
            </div>
          </div>

          <!-- Scheduled Plan Change Notice -->
          <Alert v-if="subscription.has_scheduled_change" variant="warning" class="mt-4">
            <template #title>Scheduled Plan Change</template>
            <p>
              Your plan is scheduled to change to <strong>{{ subscription.scheduled_plan?.name || 'a new plan' }}</strong> on
              <strong>{{ subscription.plan_changes_at }}</strong>. You will continue to have access to your current plan features until then.
            </p>
          </Alert>

          <!-- Billing Dates -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div v-if="subscription.is_on_trial">
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Trial Ends</p>
              <p class="text-base font-medium text-gray-900 dark:text-white">
                {{ subscription.trial_ends_at }}
              </p>
            </div>
            <div v-else>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Current Period</p>
              <p class="text-base font-medium text-gray-900 dark:text-white">
                {{ subscription.current_period_start }} - {{ subscription.current_period_end }}
              </p>
            </div>
            <div v-if="subscription.ends_at">
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Cancels On</p>
              <p class="text-base font-medium text-red-600 dark:text-red-400">
                {{ subscription.ends_at }}
              </p>
            </div>
            <div v-else-if="!subscription.is_on_trial">
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Next Billing Date</p>
              <p class="text-base font-medium text-gray-900 dark:text-white">
                {{ subscription.current_period_end }}
              </p>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              v-if="subscription.has_scheduled_change"
              variant="primary"
              size="sm"
              @click="cancelScheduledChange"
              :loading="cancellingScheduledChange"
            >
              Cancel Scheduled Change
            </Button>
            <Button
              v-else-if="subscription.is_cancelled"
              variant="primary"
              size="sm"
              @click="resumeSubscription"
              :loading="resuming"
            >
              Resume Subscription
            </Button>
            <Button
              v-else
              variant="danger"
              size="sm"
              @click="showCancelModal = true"
            >
              Cancel Subscription
            </Button>
            <Link :href="route('settings.billing.portal')">
              <Button variant="secondary" size="sm">
                Manage via Stripe
              </Button>
            </Link>
          </div>
        </div>

        <div v-else class="text-center py-8">
          <p class="text-gray-600 dark:text-gray-400 mb-4">
            You don't have an active subscription
          </p>
          <Link :href="route('settings.subscription.change-plan')">
            <Button>
              View Plans
            </Button>
          </Link>
        </div>
      </Card>

      <!-- Usage & Limits -->
      <Card v-if="subscription">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Usage & Limits
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Storage Usage -->
          <div class="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center space-x-2">
                <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Storage</span>
              </div>
            </div>
            <div class="space-y-2">
              <div class="flex items-baseline justify-between">
                <span class="text-2xl font-bold text-gray-900 dark:text-white">
                  {{ formatStorageSize(storageUsage.used_gb) }}
                </span>
                <span class="text-sm text-gray-600 dark:text-gray-400">
                  of {{ formatStorageSize(storageUsage.total_quota_gb) }}
                </span>
              </div>
              <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  class="h-2 rounded-full transition-all"
                  :class="getStorageUsageColor(storageUsage.usage_percentage)"
                  :style="{ width: Math.min(storageUsage.usage_percentage, 100) + '%' }"
                ></div>
              </div>
              <p class="text-xs text-gray-600 dark:text-gray-400">
                {{ storageUsage.usage_percentage.toFixed(1) }}% used
              </p>
            </div>
          </div>

          <!-- Work Orders / Tickets (Monthly) -->
          <div v-if="usage && usage.monthly_requests" class="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center space-x-2">
                <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Monthly Requests</span>
              </div>
            </div>
            <div class="space-y-2">
              <div class="flex items-baseline justify-between">
                <span class="text-2xl font-bold text-gray-900 dark:text-white">
                  {{ usage.monthly_requests.current || 0 }}
                </span>
                <span class="text-sm text-gray-600 dark:text-gray-400">
                  of {{ usage.monthly_requests.is_unlimited ? '∞' : usage.monthly_requests.limit }}
                </span>
              </div>
              <div v-if="!usage.monthly_requests.is_unlimited" class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  class="h-2 rounded-full bg-green-500 transition-all"
                  :style="{ width: Math.min((usage.monthly_requests.current / usage.monthly_requests.limit) * 100, 100) + '%' }"
                ></div>
              </div>
              <p class="text-xs text-gray-600 dark:text-gray-400">
                {{ usage.monthly_requests.is_unlimited ? 'Unlimited requests' : Math.round((usage.monthly_requests.current / usage.monthly_requests.limit) * 100) + '% used this month' }}
              </p>
            </div>
          </div>

          <!-- Additional Requests (Add-ons) -->
          <div v-if="hasAdditionalRequestsBalance" class="p-4 border border-purple-200 dark:border-purple-700 rounded-lg bg-purple-50 dark:bg-purple-900/20">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center space-x-2">
                <svg class="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span class="text-sm font-medium text-purple-700 dark:text-purple-300">Additional Requests</span>
              </div>
            </div>
            <div class="space-y-2">
              <div class="flex items-baseline justify-between">
                <span class="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  {{ additionalRequestsBalance }}
                </span>
                <span class="text-sm text-purple-600 dark:text-purple-400">
                  remaining
                </span>
              </div>
              <p class="text-xs text-purple-700 dark:text-purple-400">
                From purchased add-on packages
              </p>
            </div>
          </div>

          <!-- Empty State when no usage data -->
          <div v-if="!usage || Object.keys(usage).length === 0" class="col-span-full text-center py-8">
            <svg class="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0h2a2 2 0 012-2h2a2 2 0 012 2v6a2 2 0 002 2h2a2 2 0 002-2v-6a2 2 0 00-2-2m-2 0V5a2 2 0 00-2-2H9a2 2 0 00-2 2v14m6 0a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2h2z" />
            </svg>
            <p class="text-gray-600 dark:text-gray-400 mb-2">
              Usage tracking is being set up
            </p>
            <p class="text-sm text-gray-500 dark:text-gray-500">
              Your usage statistics will appear here once data collection begins
            </p>
          </div>
        </div>
      </Card>

      <!-- Storage Add-Ons -->
      <Card v-if="subscription">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
              Subscription Add-Ons
            </h2>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Expand your plan with additional storage (£5/10GB) or request packages
            </p>
            <p v-if="hasAdditionalRequestsBalance" class="text-xs text-amber-600 dark:text-amber-400 mt-2">
              <svg class="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              You can purchase additional request packages once your current package has been consumed
            </p>
          </div>
          <div class="flex flex-col space-y-2">
            <div class="flex space-x-2">
              <Button variant="primary" size="sm" @click="showAddStorageModal = true">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Add Storage
              </Button>
              <Button
                v-if="!hasAdditionalRequestsBalance"
                variant="secondary"
                size="sm"
                @click="showAddRequestsModal = true"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Add Requests
              </Button>
            </div>
          </div>
        </div>

        <!-- Add-Ons List -->
        <div class="space-y-4">

          <!-- Active Add-Ons -->
          <div
            v-for="addon in activeAddons"
            :key="addon.id"
            class="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700"
          >
            <div class="flex items-center space-x-3">
              <div
                class="w-10 h-10 rounded-lg flex items-center justify-center"
                :class="addon.addon_type === 'additional_storage' ? 'bg-green-100 dark:bg-green-900' : 'bg-purple-100 dark:bg-purple-900'"
              >
                <svg v-if="addon.addon_type === 'additional_storage'" class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <svg v-else class="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p class="font-medium text-gray-900 dark:text-white">
                  {{ addon.addon_type === 'additional_storage' ? 'Additional Storage' : 'Additional Requests' }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  {{ addon.addon_name || (addon.addon_type === 'additional_storage' ? 'Storage Add-On' : 'Request Add-On') }}
                </p>
              </div>
            </div>
            <div class="flex items-center space-x-4">
              <div class="text-right">
                <p class="font-semibold text-gray-900 dark:text-white">
                  {{ addon.quantity }} {{ addon.addon_type === 'additional_storage' ? 'GB' : 'Requests' }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  <template v-if="addon.addon_type === 'additional_storage'">
                    £{{ (addon.quantity / 10 * 5).toFixed(2) }}/month
                  </template>
                  <template v-else>
                    £{{ (parseFloat(addon.price) || 0).toFixed(2) }} (one-time)
                  </template>
                </p>
              </div>
              <Button
                variant="danger"
                size="sm"
                @click="confirmRemoveAddon(addon)"
              >
                Remove
              </Button>
            </div>
          </div>

          <!-- Scheduled Add-On Removals -->
          <div
            v-for="addon in scheduledRemovals"
            :key="addon.id"
            class="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700 opacity-60"
          >
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p class="font-medium text-gray-900 dark:text-white">
                  {{ addon.addon_type === 'additional_storage' ? 'Additional Storage' : 'Additional Requests' }} (Scheduled for Removal)
                </p>
                <p class="text-sm text-amber-600 dark:text-amber-400">
                  Will be removed on {{ formatDate(addon.expires_at) }}
                </p>
              </div>
            </div>
            <div class="text-right">
              <p class="font-semibold text-gray-900 dark:text-white">
                {{ addon.quantity }} {{ addon.addon_type === 'additional_storage' ? 'GB' : 'Requests' }}
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Pending removal
              </p>
            </div>
          </div>

          <!-- No Add-Ons Message -->
          <div v-if="activeAddons.length === 0 && scheduledRemovals.length === 0" class="text-center py-6">
            <p class="text-gray-600 dark:text-gray-400 mb-2">
              No active add-ons
            </p>
            <p class="text-sm text-gray-500 dark:text-gray-500">
              Expand your plan with additional storage or requests as your needs grow
            </p>
          </div>
        </div>
      </Card>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Payment Method -->
        <Card>
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
              Payment Method
            </h2>
            <Link :href="route('settings.billing.payment-methods.index')">
              <Button variant="secondary" size="sm">
                Manage
              </Button>
            </Link>
          </div>

          <div v-if="defaultPaymentMethod" class="space-y-4">
            <div class="flex items-center space-x-4">
              <div class="w-12 h-8 rounded border border-gray-200 dark:border-gray-700 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                <svg class="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div>
                <p class="font-medium text-gray-900 dark:text-white">
                  {{ defaultPaymentMethod.display_name }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Expires {{ defaultPaymentMethod.expiration_date }}
                </p>
              </div>
            </div>
            <Alert v-if="defaultPaymentMethod.is_expired" variant="warning">
              This payment method has expired. Please update it to avoid service interruption.
            </Alert>
          </div>

          <div v-else class="text-center py-6">
            <p class="text-gray-600 dark:text-gray-400 mb-4">
              No payment method on file
            </p>
            <Link :href="route('settings.billing.payment-methods.create')">
              <Button size="sm">
                Add Payment Method
              </Button>
            </Link>
          </div>
        </Card>

        <!-- Billing Information -->
        <Card>
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
              Billing Information
            </h2>
            <Button variant="secondary" size="sm" @click="showEditBillingModal = true">
              Edit
            </Button>
          </div>

          <div class="space-y-4">
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Company Name</p>
              <p class="text-base font-medium text-gray-900 dark:text-white">
                {{ company?.name || 'N/A' }}
              </p>
            </div>
            <div v-if="company?.billing_email">
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Billing Email</p>
              <p class="text-base font-medium text-gray-900 dark:text-white">
                {{ company?.billing_email }}
              </p>
            </div>
            <div v-if="company?.address_line_1">
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Billing Address</p>
              <p class="text-base text-gray-900 dark:text-white">
                {{ formatAddress(company) }}
              </p>
            </div>
            <div v-if="!company?.billing_email && !company?.address_line_1" class="text-center py-4">
              <p class="text-sm text-gray-500 dark:text-gray-400">
                No billing information on file
              </p>
            </div>
          </div>
        </Card>
      </div>

      <!-- Upcoming Invoice -->
      <Card v-if="upcomingInvoice">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
            Upcoming Invoice
          </h2>
          <span class="text-sm text-gray-600 dark:text-gray-400">
            Due {{ upcomingInvoice.due_date }}
          </span>
        </div>

        <div class="space-y-4">
          <!-- Line Items -->
          <div v-for="item in upcomingInvoice.lines" :key="item.id" class="flex items-center justify-between py-2">
            <div>
              <p class="font-medium text-gray-900 dark:text-white">
                {{ item.description }}
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                {{ item.period_start }} - {{ item.period_end }}
              </p>
            </div>
            <p class="font-semibold text-gray-900 dark:text-white">
              £{{ item.amount }}
            </p>
          </div>

          <!-- Total -->
          <div class="pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white">
              Total Due
            </p>
            <p class="text-2xl font-bold text-gray-900 dark:text-white">
              £{{ upcomingInvoice.total }}
            </p>
          </div>
        </div>
      </Card>

      <!-- Invoice History -->
      <Card>
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
            Recent Invoices
          </h2>
          <Link :href="route('settings.billing.invoices')">
            <Button variant="secondary" size="sm">
              View All
            </Button>
          </Link>
        </div>

        <div v-if="recentInvoices.length > 0" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr class="bg-gray-50 dark:bg-gray-800">
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Invoice
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="invoice in recentInvoices"
                :key="invoice.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <!-- Invoice Number -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex flex-col">
                    <p class="font-medium text-gray-900 dark:text-white">
                      {{ invoice.invoice_number }}
                    </p>
                    <p v-if="invoice.is_overdue" class="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                      Overdue
                    </p>
                  </div>
                </td>

                <!-- Date -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900 dark:text-white">
                    {{ invoice.invoice_date }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Due: {{ invoice.due_date }}
                  </div>
                </td>

                <!-- Status -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="getInvoiceStatusClass(invoice.status)"
                  >
                    {{ invoice.status_label }}
                  </span>
                </td>

                <!-- Amount -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-semibold text-gray-900 dark:text-white">
                    {{ invoice.formatted_total }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Tax: {{ invoice.formatted_tax }}
                  </div>
                </td>

                <!-- Actions -->
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex items-center justify-end space-x-2">
                    <!-- Pay Now Button -->
                    <Button
                      v-if="invoice.is_overdue && invoice.status === 'pending'"
                      variant="primary"
                      size="sm"
                      @click="retryPayment"
                      :loading="retrying"
                    >
                      Pay Now
                    </Button>

                    <!-- Download Button -->
                    <a
                      :href="route('settings.billing.download-invoice', invoice.id)"
                      target="_blank"
                      class="inline-flex items-center justify-center p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      title="Download Invoice PDF"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </a>

                    <!-- View Details Button -->
                    <button
                      @click="viewInvoiceDetails(invoice)"
                      class="inline-flex items-center justify-center p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      title="View Details"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="text-center py-12">
          <div class="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Invoices Yet
          </h3>
          <p class="text-gray-600 dark:text-gray-400">
            Your invoices will appear here once you have billing activity
          </p>
        </div>
      </Card>
    </div>

    <!-- Cancel Subscription Modal -->
    <Modal :show="showCancelModal" @close="showCancelModal = false">
      <template #title>Cancel Subscription</template>

      <form @submit.prevent="cancelSubscription" class="space-y-4">
        <Alert variant="warning">
          <template #title>Are you sure you want to cancel?</template>
          <div class="space-y-2">
            <p>Your subscription will be cancelled at the end of your current billing period on <strong>{{ subscription?.current_period_end }}</strong>.</p>
            <p>You will continue to have full access to all features until then. No refunds will be provided for the remaining period.</p>
          </div>
        </Alert>

        <div>
          <label for="reason" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
            Reason for cancellation (optional)
          </label>
          <textarea
            id="reason"
            v-model="cancelForm.reason"
            rows="3"
            class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="Help us improve by telling us why you're cancelling..."
          ></textarea>
        </div>

        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            @click="showCancelModal = false"
          >
            Keep Subscription
          </Button>
          <Button
            type="submit"
            variant="danger"
            :loading="cancelling"
          >
            Cancel Subscription
          </Button>
        </div>
      </form>
    </Modal>

    <!-- Add Storage Modal -->
    <Modal :show="showAddStorageModal" @close="closeAddStorageModal">
      <template #title>Add Storage</template>

      <form @submit.prevent="addStorage" class="space-y-4">
        <Alert variant="info">
          <template #title>Storage Pricing</template>
          <p>Additional storage is charged at £5 per 10GB per month. Storage must be purchased in 10GB increments. The charge will be prorated based on your current billing cycle.</p>
        </Alert>

        <div>
          <label for="storage_quantity" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
            Additional Storage (GB)
          </label>
          <input
            id="storage_quantity"
            v-model.number="addStorageForm.quantity"
            type="number"
            min="10"
            max="100"
            step="10"
            class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            placeholder="Enter GB amount (10, 20, 30...)"
          />
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Storage in 10GB increments. Maximum: 100 GB per purchase
          </p>
        </div>

        <!-- Cost Preview -->
        <div v-if="addStorageForm.quantity >= 10" class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div class="space-y-2 text-sm">
            <div class="flex items-center justify-between">
              <span class="text-gray-700 dark:text-gray-300">Storage Amount:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ addStorageForm.quantity }} GB</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-700 dark:text-gray-300">Monthly Cost:</span>
              <span class="font-medium text-gray-900 dark:text-white">£{{ (addStorageForm.quantity / 10 * 5).toFixed(2) }}/month</span>
            </div>
            <div class="flex items-center justify-between pt-2 border-t border-blue-200 dark:border-blue-700">
              <span class="text-gray-700 dark:text-gray-300">New Total Storage:</span>
              <span class="font-semibold text-gray-900 dark:text-white">
                {{ (storageUsage.total_quota_gb + addStorageForm.quantity).toFixed(0) }} GB
              </span>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            @click="closeAddStorageModal"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            :loading="addingStorage"
            :disabled="!addStorageForm.quantity || addStorageForm.quantity < 10 || addStorageForm.quantity % 10 !== 0"
          >
            Add Storage
          </Button>
        </div>
      </form>
    </Modal>

    <!-- Add Requests Modal -->
    <Modal :show="showAddRequestsModal" @close="closeAddRequestsModal">
      <template #title>Add Request Package</template>

      <form @submit.prevent="addRequests" class="space-y-4">
        <Alert variant="info">
          <template #title>Request Package Pricing</template>
          <p>Choose from our bulk request packages. All packages are one-time purchases with no recurring charges.</p>
        </Alert>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-3">
            Select Package
          </label>
          <div class="space-y-3">
            <!-- 25 Requests Package -->
            <label
              class="flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all"
              :class="addRequestsForm.package === '25' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'"
            >
              <div class="flex items-center space-x-3">
                <input
                  type="radio"
                  v-model="addRequestsForm.package"
                  value="25"
                  class="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <p class="font-semibold text-gray-900 dark:text-white">25 Requests</p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">£10.00</p>
                </div>
              </div>
              <span class="text-sm text-gray-500 dark:text-gray-400">£0.40 per request</span>
            </label>

            <!-- 50 Requests Package -->
            <label
              class="flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all"
              :class="addRequestsForm.package === '50' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'"
            >
              <div class="flex items-center space-x-3">
                <input
                  type="radio"
                  v-model="addRequestsForm.package"
                  value="50"
                  class="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <p class="font-semibold text-gray-900 dark:text-white">50 Requests</p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">£18.00</p>
                </div>
              </div>
              <span class="text-sm text-gray-500 dark:text-gray-400">£0.36 per request</span>
            </label>

            <!-- 100 Requests Package -->
            <label
              class="flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all"
              :class="addRequestsForm.package === '100' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'"
            >
              <div class="flex items-center space-x-3">
                <input
                  type="radio"
                  v-model="addRequestsForm.package"
                  value="100"
                  class="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <p class="font-semibold text-gray-900 dark:text-white">100 Requests</p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">£30.00</p>
                </div>
              </div>
              <span class="text-sm text-gray-500 dark:text-gray-400">£0.30 per request</span>
            </label>

            <!-- 250 Requests Package -->
            <label
              class="flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all"
              :class="addRequestsForm.package === '250' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'"
            >
              <div class="flex items-center space-x-3">
                <input
                  type="radio"
                  v-model="addRequestsForm.package"
                  value="250"
                  class="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <p class="font-semibold text-gray-900 dark:text-white">250 Requests</p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">£50.00</p>
                </div>
              </div>
              <span class="text-sm text-gray-500 dark:text-gray-400">£0.20 per request</span>
            </label>
          </div>
        </div>

        <!-- Cost Preview -->
        <div v-if="addRequestsForm.package" class="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <div class="space-y-2 text-sm">
            <div class="flex items-center justify-between">
              <span class="text-gray-700 dark:text-gray-300">Package:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ addRequestsForm.package }} Requests</span>
            </div>
            <div class="flex items-center justify-between pt-2 border-t border-purple-200 dark:border-purple-700">
              <span class="text-gray-700 dark:text-gray-300">Total Cost:</span>
              <span class="font-semibold text-gray-900 dark:text-white">
                £{{ getRequestPackagePrice(addRequestsForm.package) }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            @click="closeAddRequestsModal"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            :loading="addingRequests"
            :disabled="!addRequestsForm.package"
          >
            Purchase Package
          </Button>
        </div>
      </form>
    </Modal>

    <!-- Remove Addon Confirmation Modal -->
    <Modal :show="showRemoveAddonModal" @close="showRemoveAddonModal = false">
      <template #title>Remove Storage Add-On</template>

      <div class="space-y-4">
        <Alert variant="warning">
          <template #title>Confirm Removal</template>
          <p class="mb-3">
            Are you sure you want to remove <strong>{{ addonToRemove?.quantity }} GB</strong> of additional storage?
          </p>
          <p class="text-sm">
            This add-on will be removed at the end of your current billing period ({{ subscription?.current_period_end }}).
            You'll continue to have access until then, and any unused credit will be applied to future invoices.
          </p>
        </Alert>

        <div v-if="addonToRemove" class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div class="space-y-2 text-sm">
            <div class="flex items-center justify-between">
              <span class="text-gray-700 dark:text-gray-300">Storage Amount:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ addonToRemove.quantity }} GB</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-700 dark:text-gray-300">Monthly Cost:</span>
              <span class="font-medium text-gray-900 dark:text-white">
                {{ addonToRemove.formatted_price }}/month
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-700 dark:text-gray-300">Removal Date:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ subscription?.current_period_end }}</span>
            </div>
            <div class="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
              <span class="text-gray-700 dark:text-gray-300">New Total Storage:</span>
              <span class="font-semibold text-gray-900 dark:text-white">
                {{ (storageUsage.total_quota_gb - addonToRemove.quantity).toFixed(0) }} GB
              </span>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            @click="showRemoveAddonModal = false"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            @click="removeAddon"
            :loading="removingAddon"
          >
            Remove Add-On
          </Button>
        </div>
      </div>
    </Modal>

    <!-- Edit Billing Info Modal -->
    <Modal :show="showEditBillingModal" @close="showEditBillingModal = false">
      <template #title>Edit Billing Information</template>

      <form @submit.prevent="updateBillingInfo" class="space-y-4">
        <div>
          <label for="billing_email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
            Billing Email
          </label>
          <input
            id="billing_email"
            v-model="billingForm.billing_email"
            type="email"
            class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label for="address_line1" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
            Address Line 1
          </label>
          <input
            id="address_line1"
            v-model="billingForm.address_line_1"
            type="text"
            class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label for="address_line2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
            Address Line 2
          </label>
          <input
            id="address_line2"
            v-model="billingForm.address_line_2"
            type="text"
            class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="city" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
              City
            </label>
            <input
              id="city"
              v-model="billingForm.city"
              type="text"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            />
          </div>
          <div>
            <label for="state" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
              State / Province
            </label>
            <input
              id="state"
              v-model="billingForm.state_province"
              type="text"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="postal_code" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
              ZIP / Postal Code
            </label>
            <input
              id="postal_code"
              v-model="billingForm.postal_code"
              type="text"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            />
          </div>
          <div>
            <label for="country_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-2">
              Country
            </label>
            <select
              id="country_id"
              v-model="billingForm.country_id"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            >
              <option :value="null">Select a country</option>
              <option v-for="country in countries" :key="country.id" :value="country.id">
                {{ country.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            @click="showEditBillingModal = false"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            :loading="updatingBilling"
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>

    <!-- Invoice Details Modal -->
    <Modal :show="showInvoiceDetailsModal" @close="showInvoiceDetailsModal = false" size="lg">
      <template #title>Invoice Details</template>

      <div v-if="selectedInvoice" class="space-y-6">
        <!-- Invoice Header -->
        <div class="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ selectedInvoice.invoice_number }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ selectedInvoice.invoice_date }}
            </p>
          </div>
          <span
            class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
            :class="getInvoiceStatusClass(selectedInvoice.status)"
          >
            {{ selectedInvoice.status_label }}
          </span>
        </div>

        <!-- Invoice Summary -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Subtotal</p>
            <p class="text-base font-medium text-gray-900 dark:text-white">
              {{ selectedInvoice.formatted_subtotal }}
            </p>
          </div>
          <div>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Tax</p>
            <p class="text-base font-medium text-gray-900 dark:text-white">
              {{ selectedInvoice.formatted_tax }}
            </p>
          </div>
        </div>

        <!-- Total -->
        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <p class="text-lg font-semibold text-gray-900 dark:text-white">Total</p>
            <p class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ selectedInvoice.formatted_total }}
            </p>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" @click="showInvoiceDetailsModal = false">
            Close
          </Button>
          <a
            :href="route('settings.billing.download-invoice', selectedInvoice.id)"
            target="_blank"
          >
            <Button>
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download PDF
            </Button>
          </a>
        </div>
      </div>
    </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, router, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Alert from '@/Components/Common/Alert.vue';
import Modal from '@/Components/Common/Modal.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';

// Props
const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
  subscription: {
    type: Object,
    default: null,
  },
  paymentMethods: {
    type: Array,
    default: () => [],
  },
  upcomingInvoice: {
    type: Object,
    default: null,
  },
  recentInvoices: {
    type: Array,
    default: () => [],
  },
  addons: {
    type: Array,
    default: () => [],
  },
  storageUsage: {
    type: Object,
    default: () => ({
      used_gb: 0,
      total_quota_gb: 0,
      available_gb: 0,
      usage_percentage: 0,
    }),
  },
  usage: {
    type: Object,
    default: () => ({}),
  },
  countries: {
    type: Array,
    default: () => [],
  },
  error: {
    type: String,
    default: null,
  },
});

// Composables
const { success, error: showError } = useToast();

// Refs
const showCancelModal = ref(false);
const showEditBillingModal = ref(false);
const showAddStorageModal = ref(false);
const showAddRequestsModal = ref(false);
const showRemoveAddonModal = ref(false);
const showInvoiceDetailsModal = ref(false);
const cancelling = ref(false);
const resuming = ref(false);
const cancellingScheduledChange = ref(false);
const retrying = ref(false);
const updatingBilling = ref(false);
const addingStorage = ref(false);
const addingRequests = ref(false);
const removingAddon = ref(false);
const addonToRemove = ref(null);
const selectedInvoice = ref(null);

// Forms
const cancelForm = useSecureForm({
  reason: '',
});

const resumeForm = useSecureForm({});

const cancelScheduledChangeForm = useSecureForm({});

const billingForm = useSecureForm({
  billing_email: props.company?.billing_email || '',
  address_line_1: props.company?.address_line_1 || '',
  address_line_2: props.company?.address_line_2 || '',
  city: props.company?.city || '',
  state_province: props.company?.state_province || '',
  postal_code: props.company?.postal_code || '',
  country_id: props.company?.country_id || null,
});

const addStorageForm = ref({
  quantity: 10,
});

const addRequestsForm = ref({
  package: null,
});

// Computed
const defaultPaymentMethod = computed(() => {
  return props.paymentMethods.find(method => method.is_default);
});

const activeAddons = computed(() => {
  return props.addons.filter(addon => addon.status === 'active' && !addon.expires_at);
});

const scheduledRemovals = computed(() => {
  return props.addons.filter(addon => addon.status === 'active' && addon.expires_at);
});

const additionalRequestsBalance = computed(() => {
  return props.addons
    .filter(addon => addon.addon_type === 'additional_requests' && addon.status === 'active' && addon.is_consumable)
    .reduce((total, addon) => total + (addon.balance_remaining || 0), 0);
});

const hasAdditionalRequestsBalance = computed(() => {
  return additionalRequestsBalance.value > 0;
});

// Methods
const getStatusClass = (status) => {
  const classes = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    trialing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    past_due: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  };
  return classes[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
};

const getInvoiceStatusClass = (status) => {
  const classes = {
    paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    voided: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  };
  return classes[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
};

const formatAddress = (company) => {
  if (!company) return 'N/A';

  const parts = [
    company.address_line_1,
    company.address_line_2,
    company.city,
    company.state_province,
    company.postal_code,
    company.country_name,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : 'N/A';
};

const cancelSubscription = () => {
  cancelling.value = true;

  cancelForm.patch(route('settings.billing.cancel-subscription'), {
    preserveScroll: true,
    onSuccess: () => {
      success('Subscription cancelled successfully');
      showCancelModal.value = false;
      cancelForm.reset();
    },
    onError: () => {
      showError('Failed to cancel subscription');
    },
    onFinish: () => {
      cancelling.value = false;
    },
  });
};

const resumeSubscription = () => {
  resuming.value = true;

  resumeForm.patch(route('settings.billing.resume-subscription'), {
    preserveScroll: true,
    onSuccess: () => {
      success('Subscription resumed successfully');
    },
    onError: () => {
      showError('Failed to resume subscription');
    },
    onFinish: () => {
      resuming.value = false;
    },
  });
};

const cancelScheduledChange = () => {
  cancellingScheduledChange.value = true;

  cancelScheduledChangeForm.patch(route('settings.billing.cancel-scheduled-change'), {
    preserveScroll: true,
    onSuccess: () => {
      success('Scheduled plan change cancelled successfully');
    },
    onError: () => {
      showError('Failed to cancel scheduled plan change');
    },
    onFinish: () => {
      cancellingScheduledChange.value = false;
    },
  });
};

const retryPayment = async () => {
  retrying.value = true;

  try {
    const response = await axios.post(route('api.settings.billing.retry-payment'));

    if (response.data.success) {
      success('Payment processed successfully');
      router.reload({ only: ['recentInvoices', 'subscription'] });
    } else {
      showError(response.data.message || 'Payment failed');
    }
  } catch (err) {
    showError('Failed to process payment');
  } finally {
    retrying.value = false;
  }
};

const updateBillingInfo = () => {
  updatingBilling.value = true;

  billingForm.patch(route('settings.billing.update-info'), {
    preserveScroll: true,
    onSuccess: () => {
      success('Billing information updated successfully');
      showEditBillingModal.value = false;
    },
    onError: () => {
      showError('Failed to update billing information');
    },
    onFinish: () => {
      updatingBilling.value = false;
    },
  });
};

const formatStorageSize = (gb) => {
    const numGb = parseFloat(gb);
    if (!numGb || numGb === 0 || isNaN(numGb)) return '0 GB';
    return `${numGb.toFixed(2)} GB`;
  };

const getStorageUsageColor = (percentage) => {
  if (percentage >= 90) return 'bg-red-500';
  if (percentage >= 75) return 'bg-amber-500';
  return 'bg-blue-500';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const closeAddStorageModal = () => {
  showAddStorageModal.value = false;
  addStorageForm.value.quantity = 10;
};

const closeAddRequestsModal = () => {
  showAddRequestsModal.value = false;
  addRequestsForm.value.package = null;
};

const getRequestPackagePrice = (packageSize) => {
  const prices = {
    '25': '10.00',
    '50': '18.00',
    '100': '30.00',
    '250': '50.00',
  };
  return prices[packageSize] || '0.00';
};

const addRequests = async () => {
  if (!addRequestsForm.value.package) {
    showError('Please select a package');
    return;
  }

  addingRequests.value = true;

  try {
    const response = await axios.post(route('api.settings.billing.purchase-addon'), {
      addon_type: 'requests',
      package: addRequestsForm.value.package,
    });

    if (response.data.success) {
      success(response.data.message || 'Request package purchased successfully');
      closeAddRequestsModal();
      router.reload({ only: ['addons', 'company'] });
    } else {
      showError(response.data.message || 'Failed to purchase request package');
    }
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to purchase request package. Please try again.';
    showError(errorMessage);
  } finally {
    addingRequests.value = false;
  }
};

const addStorage = async () => {
  if (!addStorageForm.value.quantity || addStorageForm.value.quantity < 10 || addStorageForm.value.quantity % 10 !== 0) {
    showError('Please enter a valid storage amount in 10GB increments (10, 20, 30, etc.)');
    return;
  }

  addingStorage.value = true;

  try {
    const response = await axios.post(route('api.settings.billing.purchase-addon'), {
      addon_type: 'storage',
      quantity: addStorageForm.value.quantity,
    });

    if (response.data.success) {
      success(response.data.message || 'Storage added successfully');
      closeAddStorageModal();
      router.reload({ only: ['addons', 'storageUsage', 'company'] });
    } else {
      showError(response.data.message || 'Failed to add storage');
    }
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to add storage. Please try again.';
    showError(errorMessage);
  } finally {
    addingStorage.value = false;
  }
};

const confirmRemoveAddon = (addon) => {
  addonToRemove.value = addon;
  showRemoveAddonModal.value = true;
};

const removeAddon = async () => {
  if (!addonToRemove.value) return;

  removingAddon.value = true;

  try {
    const response = await axios.delete(route('api.settings.billing.remove-addon', addonToRemove.value.id));

    if (response.data.success) {
      success(response.data.message || 'Add-on removal scheduled successfully');
      showRemoveAddonModal.value = false;
      addonToRemove.value = null;
      router.reload({ only: ['addons', 'storageUsage'] });
    } else {
      showError(response.data.message || 'Failed to remove add-on');
    }
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to remove add-on. Please try again.';
    showError(errorMessage);
  } finally {
    removingAddon.value = false;
  }
};

const viewInvoiceDetails = (invoice) => {
  selectedInvoice.value = invoice;
  showInvoiceDetailsModal.value = true;
};
</script>
